import { FormEvent, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import update from 'immutability-helper'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import sharedClasses from '@modals/index.module.css'
import { contractTemplateApi } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplate.api'
import { usersAPI } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import type { StudentContractState } from '../AddContractModal'
import TemplateContractForm from './components/TemplateContractForm'
import { useContractTemplates } from './hooks/useContractTemplates'
import { useTemplateConstructor } from './hooks/useTemplateConstructor'
import {
  buildCreateContractPayload,
  getEmptyRequiredFieldCodes,
} from './utils/contractTemplateConstructor.utils'
import { getErrorText } from './utils/getErrorText'
import classes from './AddTemplateContractModal.module.css'

export type AddTemplateContractModalProps = {
  active?: boolean
  close?: () => void
  state: StudentContractState
  setState: (value: StudentContractState) => void
}

const AddTemplateContractModal = ({
  active = true,
  close,
  state,
  setState,
}: AddTemplateContractModalProps) => {
  const addAlert = useAddAlert()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { templatesLoading, templateOptions } = useContractTemplates(active)

  const {
    templateId,
    constructorData,
    constructorGroups,
    constructorLoading,
    parentLoading,
    productLoading,
    documentName,
    setDocumentName,
    values,
    selectedParentId,
    selectedProductKey,
    invalidFieldCodes,
    setInvalidFieldCodes,
    recalculatingCodes,
    parentOptions,
    productOptions,
    shouldShowProductSelector,
    handleTemplateChange,
    handleParentChange,
    handleProductChange,
    handleFieldChange,
  } = useTemplateConstructor({
    active,
    userId: state?.id,
  })

  const refreshContracts = async () => {
    const userRes = await usersAPI.getUser({
      id: state.id,
      expand: ['contracts'],
    })

    setState(
      update(state, {
        contracts: { $set: userRes.data.contracts },
      }),
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (templateId == null || !state?.id || !constructorData) {
      return
    }

    const emptyFields = getEmptyRequiredFieldCodes(constructorGroups, values)

    if (!documentName.trim()) {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите название договора',
        type: 'error',
      })
      return
    }

    if (emptyFields.length) {
      setInvalidFieldCodes(new Set(emptyFields))
      addAlert({
        title: 'Ошибка',
        text: 'Заполните все поля, используемые в шаблоне договора',
        type: 'error',
      })
      return
    }

    setIsSubmitting(true)

    try {
      await contractTemplateApi.createContractFromTemplate(
        buildCreateContractPayload({
          user_id: Number(state.id),
          template_id: Number(templateId),
          document_name: documentName.trim(),
          parent_id: selectedParentId,
          productKey: selectedProductKey,
          values,
        }),
      )

      await refreshContracts()
      addAlert({ title: 'Договор успешно создан' })
      close?.()
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: getErrorText(error, 'Не удалось создать договор из шаблона'),
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!active) {
    return null
  }

  return createPortal(
    <Modal
      active={active}
      setActive={close}
      className={classes.modalWrapper as any}
      style={{ zIndex: '110' }}
      styleContent={{
        minWidth: 'auto',
        padding: 0,
      }}
    >
      <div
        className={classNames(
          sharedClasses.modalContainer,
          classes.modalContainer,
        )}
      >
        <div className={sharedClasses.modalHeader}>
          <div className={sharedClasses.modalTitleContainer}>
            <span className={sharedClasses.modalHeaderBigTitle}>
              Загрузка шаблонного договора
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={close}>
            <IcoRemove />
          </div>
        </div>

        <TemplateContractForm
          templateId={templateId}
          templateOptions={templateOptions}
          templatesLoading={templatesLoading}
          constructorLoading={constructorLoading}
          parentLoading={parentLoading}
          productLoading={productLoading}
          isSubmitting={isSubmitting}
          documentName={documentName}
          values={values}
          invalidFieldCodes={invalidFieldCodes}
          recalculatingCodes={recalculatingCodes}
          constructorGroups={constructorGroups}
          parentOptions={parentOptions}
          productOptions={productOptions}
          shouldShowProductSelector={shouldShowProductSelector}
          selectedParentId={selectedParentId}
          selectedProductKey={selectedProductKey}
          hasConstructorData={Boolean(constructorData)}
          onTemplateChange={handleTemplateChange}
          onParentChange={handleParentChange}
          onProductChange={handleProductChange}
          onDocumentNameChange={setDocumentName}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
        />
      </div>
    </Modal>,
    document.body,
  )
}

export default AddTemplateContractModal
